import { T, useGT } from 'gt-react';
import type { TooltipContentProps } from 'recharts';
import {
  compactPurchaseTrendCurrency,
  type PurchaseTrendChartRow,
} from './purchaseTrendChart';
type TooltipItem = NonNullable<
  TooltipContentProps<number, string>['payload']
>[number];

function itemByDataKey(
  payload: NonNullable<TooltipContentProps<number, string>['payload']>,
  dataKey: string,
): TooltipItem | undefined {
  return payload.find((item) => item.dataKey === dataKey);
}

function TooltipAmountLine({
  testId,
  color,
  name,
  value,
}: {
  testId: string;
  color: string | undefined;
  name: unknown;
  value: unknown;
}) {
  return (
    <p data-testid={testId} style={{ color }}>
      {String(name)}: {compactPurchaseTrendCurrency(Number(value ?? 0))}
    </p>
  );
}

function PurchaseTrendReportedTooltipLine({
  row,
  pastItem,
  futureItem,
}: {
  row: PurchaseTrendChartRow | undefined;
  pastItem: TooltipItem | undefined;
  futureItem: TooltipItem | undefined;
}) {
  const gt = useGT();
  if (row?.reported != null) {
    return (
      <TooltipAmountLine
        testId="purchase-trend-tooltip-reported"
        color={pastItem?.color}
        name={gt('Reported (Black)', {
          context:
            'rebate forecast chart legend: actual reported amounts line color black',
        })}
        value={row.reported}
      />
    );
  }
  if (row?.reported_future != null) {
    return (
      <TooltipAmountLine
        testId="purchase-trend-tooltip-reported"
        color={futureItem?.color}
        name={gt('Reported (Future)', {
          context: 'rebate forecast chart legend: extended reported projection',
        })}
        value={row.reported_future}
      />
    );
  }
  return null;
}

export function PurchaseTrendTooltip({
  active,
  payload,
  label,
}: Partial<TooltipContentProps<number, string>>) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const row = payload[0]?.payload as PurchaseTrendChartRow | undefined;
  const volume = itemByDataKey(payload, 'reportedVolume');
  const purchases = itemByDataKey(payload, 'purchases');
  const priorYear = itemByDataKey(payload, 'priorYearPurchases');
  return (
    <div
      className="rounded-md border bg-background px-3 py-2 text-sm shadow-sm"
      data-testid="purchase-trend-tooltip"
    >
      <p className="font-medium" data-testid="purchase-trend-tooltip-month">
        {label}
      </p>
      {purchases ? (
        <TooltipAmountLine
          testId="purchase-trend-tooltip-purchases"
          color={purchases.color}
          name={purchases.name}
          value={purchases.value}
        />
      ) : null}
      <PurchaseTrendReportedTooltipLine
        row={row}
        pastItem={itemByDataKey(payload, 'reported')}
        futureItem={itemByDataKey(payload, 'reported_future')}
      />
      {priorYear ? (
        <TooltipAmountLine
          testId="purchase-trend-tooltip-prior-year"
          color={priorYear.color}
          name={priorYear.name}
          value={priorYear.value}
        />
      ) : null}
      {volume ? (
        <TooltipAmountLine
          testId="purchase-trend-tooltip-reported-volume"
          color={volume.color}
          name={volume.name}
          value={volume.value}
        />
      ) : null}
      {row?.isPartialMonth ? (
        <p className="mt-1 text-xs text-muted-foreground">
          <T context="chart tooltip note for a partial purchase month">
            Partial through-month
          </T>
        </p>
      ) : null}
    </div>
  );
}
